import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext();

/* ── Accent colour presets ── */
export const ACCENT_PRESETS = {
  cyan:    { label: 'Cyan',    primary: '#00d4ff', secondary: '#7c3aed', rgb: '0,212,255'   },
  violet:  { label: 'Violet',  primary: '#8b5cf6', secondary: '#6366f1', rgb: '139,92,246'   },
  emerald: { label: 'Emerald', primary: '#10b981', secondary: '#059669', rgb: '16,185,129'   },
  rose:    { label: 'Rose',    primary: '#f43f5e', secondary: '#ec4899', rgb: '244,63,94'    },
  amber:   { label: 'Amber',   primary: '#f59e0b', secondary: '#d97706', rgb: '245,158,11'   },
  ocean:   { label: 'Ocean',   primary: '#0ea5e9', secondary: '#2563eb', rgb: '14,165,233'   },
};

/* ── Apply variables to <html> ── */
function applyTheme(mode, accent) {
  const root = document.documentElement;
  root.setAttribute('data-theme', mode);
  root.setAttribute('data-accent', accent);

  const preset = ACCENT_PRESETS[accent] || ACCENT_PRESETS.cyan;

  // Override the "cyan" family so all existing var(--accent-cyan) references adapt
  root.style.setProperty('--accent-cyan', preset.primary);
  root.style.setProperty('--accent-primary', preset.primary);
  root.style.setProperty('--accent-primary-rgb', preset.rgb);
  root.style.setProperty('--gradient-primary', `linear-gradient(135deg, ${preset.primary} 0%, ${preset.secondary} 100%)`);
  root.style.setProperty('--shadow-glow-cyan', `0 0 30px rgba(${preset.rgb}, 0.15)`);
  root.style.setProperty('--border-accent', `rgba(${preset.rgb}, 0.3)`);
  root.style.setProperty('--text-accent', preset.primary);
}

/* ── Apply immediately on module load (prevents light flash) ── */
const INITIAL_MODE = localStorage.getItem('dg-theme-mode') || 'dark';
const INITIAL_ACCENT = localStorage.getItem('dg-theme-accent') || 'cyan';
applyTheme(INITIAL_MODE, INITIAL_ACCENT);

export function ThemeProvider({ children }) {
  const [mode, setMode] = useState(INITIAL_MODE);
  const [accent, setAccent] = useState(INITIAL_ACCENT);

  // Keep in sync whenever mode/accent changes
  useEffect(() => {
    applyTheme(mode, accent);
    localStorage.setItem('dg-theme-mode', mode);
    localStorage.setItem('dg-theme-accent', accent);
  }, [mode, accent]);

  const toggleMode = useCallback(() => {
    setMode(prev => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  const value = { mode, accent, setMode, setAccent, toggleMode };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside ThemeProvider');
  return ctx;
}
